import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LuMessageSquare, LuExternalLink, LuSearch, LuRefreshCw, LuArrowDownAZ } from "react-icons/lu";
import { AppContext } from "../../utils/Context/AppContext";
import { getMkbDocuments, refreshMkbDocuments } from "../../utils/services/api";

const formatLastSynced = (iso) => {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// Splits "3012-2019 4_Preface.pdf" into { base: "3012-2019 4_Preface", ext: "PDF" }
// so the extension can be shown as its own small badge instead of sitting
// inline in the readable name.
const splitNameExt = (name) => {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) return { base: name, ext: null };
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot + 1).toUpperCase() };
};

// Documents categorised as Australian Standards are shown/searchable with an
// "AS " prefix (e.g. "AS 3012-2019 4_Preface.pdf") — how members are used to
// seeing these referenced. Display-only: never written back to SQL/SharePoint.
// Only added if "AS" doesn't already appear anywhere in the name — real
// filenames vary too much in how they already reference AS/AS_NZS (leading
// "AS ", "AS_NZS", etc.) to reliably pattern-match every case, so the simple
// rule is: if "AS" is in there already in any form, leave the name alone.
const AUSTRALIAN_STANDARDS_CATEGORY = "australian standards";
const displayName = (doc) =>
  (doc.category || "").trim().toLowerCase() === AUSTRALIAN_STANDARDS_CATEGORY &&
  !/AS/i.test(doc.name)
    ? `AS ${doc.name}`
    : doc.name;

const PAGE_SIZE = 25;

// Fixed palette cycled by category name (hashed) so a given category always
// gets the same badge color across a session, without a rainbow of
// arbitrarily-assigned hues.
const BADGE_COLORS = [
  { bg: "#EFFBFF", text: "#00AEEF" },
  { bg: "#F3EFFF", text: "#7C5CFC" },
  { bg: "#FFF4E5", text: "#E3922B" },
  { bg: "#E9F9EE", text: "#2FA360" },
  { bg: "#FFEDF0", text: "#E0507A" },
];
const colorForCategory = (category) => {
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[hash % BADGE_COLORS.length];
};

const MemberKnowledgeBase = () => {
  const { isAdmin } = useContext(AppContext);

  const [documents, setDocuments] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]); // empty = all categories
  const [sortBy, setSortBy] = useState("category"); // "category" (default, matches SQL order) | "name"

  const toggleCategory = (name) => {
    setSelectedCategories((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };
  const [page, setPage] = useState(1);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(false);
      const { documents: docs, lastSynced: synced } = await getMkbDocuments();
      setDocuments(docs);
      setLastSynced(synced);
    } catch (err) {
      setError(true);
      toast.error("Failed to load Member Knowledge Base documents");
    } finally {
      setLoading(false);
    }
  };

  // The backend kicks this off in the background and responds immediately
  // (a synchronous multi-minute call here used to hit nginx's gateway
  // timeout in prod). So instead of awaiting a result, poll the fast GET
  // endpoint until its last_synced timestamp actually changes, then reload
  // the list. Gives up after 15 minutes rather than polling forever if
  // something's gone wrong server-side.
  const handleRefresh = async () => {
    const previousSynced = lastSynced;
    try {
      setRefreshing(true);
      await refreshMkbDocuments();
      toast("Refresh started — this can take a few minutes…", { icon: "⏳" });

      const POLL_INTERVAL_MS = 15000;
      const MAX_WAIT_MS = 15 * 60 * 1000;
      const startedAt = Date.now();
      let finished = false;
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        const { documents: docs, lastSynced: synced } = await getMkbDocuments();
        if (synced && synced !== previousSynced) {
          setDocuments(docs);
          setLastSynced(synced);
          toast.success("Member Knowledge Base refreshed");
          finished = true;
          break;
        }
      }
      if (!finished) {
        toast("Still refreshing in the background — check back shortly", { icon: "⏳" });
      }
    } catch (err) {
      toast.error("Failed to start refresh");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // One pass over all documents to get both the category list AND how many
  // documents are in each — powers the quick-filter chips, the dropdown
  // labels, and the "X categories" stat, all from a single computation.
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    for (const d of documents) {
      const key = d.category || "Uncategorized";
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = documents.filter((d) => {
      const matchesCategory =
        selectedCategories.length === 0 || selectedCategories.includes(d.category || "Uncategorized");
      const matchesSearch = !q || displayName(d).toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
    if (sortBy === "name") {
      result = [...result].sort((a, b) => displayName(a).localeCompare(displayName(b)));
    }
    return result;
  }, [documents, search, selectedCategories, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedCategories, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex flex-col gap-6 h-full w-full bg-slate-100 p-3  overflow-hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[#3A3A3A]">Member Knowledge Base</h1>
        <Link
          to="/chat?new_conversation"
          className="text-[#00AEEF] text-sm flex items-center gap-2 font-semibold px-3 py-2 hover:bg-[#00AEEF] hover:text-white bg-white rounded-[4px] border border-[#00AEEF] transition-colors duration-300 ease-in-out"
        >
          <LuMessageSquare size={16} />
          Chat
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full flex-1 flex flex-col overflow-auto min-h-36">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-1 shrink-0">
          <h2 className="font-semibold text-[#3A3A3A]">Documents</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
            <span className="text-xs text-[#616161]">Last synced: {formatLastSynced(lastSynced)}</span>
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              title="Pulls the latest documents — can take a few minutes"
              className="flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00AEEF] text-[#00AEEF] hover:bg-[#EFFBFF] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            >
              <LuRefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-[#616161] mb-4 shrink-0">
          Every document referenced across Niki's knowledge base, organised by category.
          Click a document to open its reference link in a new tab.
        </p>
        {loading ? (
          <p className="text-sm text-[#616161]">Loading…</p>
        ) : error ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-[#616161]">Couldn't load documents.</p>
            <button onClick={loadDocuments} className="text-xs font-semibold text-[#00AEEF] hover:underline">
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center gap-10 md:gap-16 py-2 mb-4 border-b border-[#EEEBEB] pb-4 shrink-0">
              <div>
                <p className="text-4xl font-bold text-[#3A3A3A]">{documents.length.toLocaleString()}</p>
                <p className="text-xs text-[#616161] mt-1">Total documents</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#3A3A3A]">{categoryCounts.length.toLocaleString()}</p>
                <p className="text-xs text-[#616161] mt-1">Categories</p>
              </div>
            </div>
            {/* Category quick-filter tabs — click to toggle a category on/off,
                click several to filter by multiple at once. Same idea as the
                tiles on the public MKB page, in our own pill/badge language
                and with multi-select. "All" clears the selection back to
                showing every category. */}
            <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 shrink-0 hide-scrollbar">
              <button
                onClick={() => setSelectedCategories([])}
                className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  selectedCategories.length === 0
                    ? "bg-[#00AEEF] border-[#00AEEF] text-white"
                    : "border-[#EEEBEB] text-[#616161] hover:border-[#00AEEF] hover:text-[#00AEEF]"
                }`}
              >
                All ({documents.length})
              </button>
              {categoryCounts.map(({ name, count }) => {
                const color = colorForCategory(name);
                const active = selectedCategories.includes(name);
                return (
                  <button
                    key={name}
                    onClick={() => toggleCategory(name)}
                    aria-pressed={active}
                    className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                    style={
                      active
                        ? { backgroundColor: color.text, borderColor: color.text, color: "#fff" }
                        : { backgroundColor: color.bg, borderColor: color.bg, color: color.text }
                    }
                  >
                    {name} ({count})
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between mb-4 shrink-0">
              <div className="relative flex-1 max-w-md">
                <LuSearch size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search documents"
                  className="w-full border border-[#DCDCDC] rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[#00AEEF]"
                />
              </div>
              <button
                onClick={() => setSortBy((s) => (s === "name" ? "category" : "name"))}
                title="Toggle sort order"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-[#EEEBEB] text-[#3A3A3A] bg-white hover:border-[#00AEEF] shrink-0"
              >
                <LuArrowDownAZ size={14} />
                Sort: {sortBy === "name" ? "Name" : "Category"}
              </button>
            </div>

            {filtered.length === 0 ? (
              <p className="text-sm text-[#616161] flex-1 flex items-center justify-center text-center">
                No documents match{search.trim() ? ` "${search.trim()}"` : ""}
                {selectedCategories.length > 0 ? ` in ${selectedCategories.join(", ")}` : ""}.
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-2 flex-1 overflow-auto h-[500px]">
                  {pageItems.map((doc, idx) => {
                    const color = colorForCategory(doc.category || "Uncategorized");
                    const { base } = splitNameExt(displayName(doc));
                    return (
                      <a
                        key={`${doc.name}-${idx}`}
                        href={doc.mkb_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border border-[#EEEBEB] rounded-md px-4 py-3 hover:bg-[#FAFEFF] hover:border-[#00AEEF] cursor-pointer transition-colors shrink-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-[#242424] break-words">{base}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            className="text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ backgroundColor: color.bg, color: color.text }}
                          >
                            {doc.category || "Uncategorized"}
                          </span>
                          <LuExternalLink size={14} className="text-[#9A9A9A]" />
                        </div>
                      </a>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#EEEBEB] shrink-0">
                  <p className="text-xs text-[#616161]">
                    Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
                    {filtered.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00AEEF] text-[#00AEEF] hover:bg-[#EFFBFF] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-[#616161]">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00AEEF] text-[#00AEEF] hover:bg-[#EFFBFF] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberKnowledgeBase;
